import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";

interface Props {}
export const PaperspaceProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const region = useCpFormValue(`paperspace.region`);
  const networkId = useCpFormValue(`paperspace.networkId`);
  const sharedDrives = useCpFormValue(`paperspace.sharedDrives`);
  const userIds = useCpFormValue(`paperspace.userIds`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Paperspace Provider
            <CpErrorWarningArrayIcon
              names={[
                `paperspace.region`,
                `paperspace.tokenSecretLink`,
                `paperspace.sharedDrives`,
                `paperspace.autoscaler`,
                `paperspace.preInstallScript`,
                `paperspace.userIds`,
                `paperspace.networkId`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`paperspace.region`}>Region</CpLabel>
              <Space.Compact>
                <CpInput name={`paperspace.region`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("regions")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`paperspace.region`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`paperspace.tokenSecretLink`}>Paperspace Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Paperspace token, it will be used to manage servers in your Paperspace account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`paperspace.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-paperspace-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `paperspace.tokenSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`paperspace.tokenSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`paperspace.networkId`}>Network ID</CpLabel>
              <Space.Compact>
                <CpInput name={`paperspace.networkId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("networks")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`paperspace.networkId`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label="Shared Drives" name={`paperspace.sharedDrives`} />
            </div>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label="User IDs" name={`paperspace.userIds`} />
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <ProviderPreInstallScript provider="paperspace" />
              <ProviderAutoscaler provider="paperspace" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "regions" ? (
        <NGProviderBrowser
          title="Browse Paperspace Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `paperspace.region`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.name, item.name, item.description]}
        />
      ) : null}
      {browserKey === "networks" ? (
        <NGProviderBrowser
          title="Browse Paperspace Networks"
          initialValue={networkId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "networks",
                cluster: inputToMk8s(values),
              },
            });
            return data.items;
          }}
          onOk={(value) => {
            formSetValue(id, `paperspace.networkId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Id", "Name"]}
          getData={(item: any) => [item.id, item.id, item.name]}
        />
      ) : null}
      {browserKey === "shared-drives" ? (
        <NGProviderBrowser
          title="Browse Paperspace Shared Drives"
          initialValue={sharedDrives.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "shared-drives",
                cluster: inputToMk8s(values),
              },
            });
            return data.items;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[id];
            const formValues: Mk8sInput = form.values as any;

            const _sharedDrives = cloneDeep(formValues.paperspace.sharedDrives);
            for (const value of values) {
              if (_sharedDrives.some((i) => i.value === value)) {
                continue;
              }
              _sharedDrives.push({ id: uuidv4(), value: value });
            }
            formSetValue(id, `paperspace.sharedDrives`, _sharedDrives);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [String(item.name), item.name]}
        />
      ) : null}
      {browserKey === "users" ? (
        <NGProviderBrowser
          title="Browse Paperspace Users"
          initialValue={userIds.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "users",
                cluster: inputToMk8s(values),
              },
            });
            return data.items;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[id];
            const formValues: Mk8sInput = form.values as any;

            const _userIds = cloneDeep(formValues.paperspace.userIds);
            for (const value of values) {
              if (_userIds.some((i) => i.value === value)) {
                continue;
              }
              _userIds.push({ id: uuidv4(), value: value });
            }
            formSetValue(id, `paperspace.userIds`, _userIds);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Id", "Email", "Full Name"]}
          getData={(item: any) => [
            String(item.user.id),
            item.user.id,
            item.user.email,
            item.user.firstName + " " + item.user.lastName,
          ]}
        />
      ) : null}
    </>
  );
};
